<template>
  <div>
    <div class="row extra-stops margin-b-m" v-if="showLocs">
      <div class="column end padding-r-s small-padding-h-z medium-12 large-4">
        <label class="field-label">{{ $t('serviceSelect.pickup') }}</label>
        <div class="field-group">
          <location-input :value="pickup" :extra="pickupExtra" ref="pickup"
            :placeId="pickupPlaceId" :disabled="false" error="" :leg="leg"
            @change="locationNewValue"
          ></location-input>
        </div>
      </div>

      <div class="column end padding-r-s small-padding-h-z medium-12 large-4" v-for="stop in extraStop" :key="stop.id">
        <label class="field-label">{{ $t('serviceSelect.extraStop') }}</label>
        <div class="field-group">
          <location-input :value="stop.text" :extra="stop.extra" :ref="`extraStop${stop.id}`"
            :placeId="stop.placeId" :disabled="false" :error="stop.error" :id="stop.id" :leg="leg"
          ></location-input>
        </div>
      </div>

      <div class="column end padding-r-s small-padding-h-z medium-12 large-4">
        <label class="field-label">{{ $t('serviceSelect.dropoff') }}</label>
        <div class="field-group">
          <location-input :value="dropoff" :extra="dropoffExtra" ref="dropoff"
            :placeId="dropoffPlaceId" :disabled="false" error="" :leg="leg"
            @change="locationNewValue"
          ></location-input>
        </div>
      </div>
      <span class="es-notice" v-if="extraStop.length">* {{ $t('serviceSelect.sharedNotAllowed') }}</span>
    </div>

    <div class="row" v-if="charter.enabled">
      <div class="medium-12 columns end padding-r-s margin-b-m small-padding-h-z large-4">
        <div :class="{'field-group':true, 'field-error':!!charterValidation.startDate}">
          <label class="field-label">{{ $t('serviceSelect.startDate') }}</label>
          <input type="text" @click="openCharterPicker('startDate')" :disabled="serviceLoading"
            :class="['field-text', {highlight:highlight === 'startDate', disabled:serviceLoading}]"
            v-model="charter.startDate" ref="startDate" readonly />
          <div class="text-right error-box"><span class="field-error-message">{{ charterValidation.startDate }}</span></div>
        </div>
      </div>

      <div class="medium-12 columns end padding-r-s margin-b-m small-padding-h-z large-4">
        <div :class="{'field-group':true, 'field-error':!!charterValidation.endDate}">
          <label class="field-label">{{ $t('serviceSelect.endDate') }}</label>
          <input type="text" @click="openCharterPicker('endDate')" :disabled="serviceLoading"
            :class="['field-text', {highlight:highlight === 'endDate', disabled:serviceLoading}]"
            v-model="charter.endDate" ref="endDate" readonly />
          <div class="text-right error-box"><span class="field-error-message">{{ charterValidation.endDate }}</span></div>
        </div>
      </div>

      <div class="medium-12 columns end padding-r-s margin-b-m small-padding-h-z large-4"
        v-if="isAirportPickup">
        <div :class="{'field-group':true, 'field-error':!!validation.pickupDate}">
          <label class="field-label">{{ $t('serviceSelect.flightArrive') }}</label>
          <input type="text" placeholder="Scheduled" @click="openPicker"
            :class="{'field-text':true, 'highlight':highlight === 'pickupDate'}"
            v-model="pickupDate" ref="datepicker" readonly />
          <div class="text-right error-box"><span class="field-error-message">{{ validation.pickupDate }}</span></div>
        </div>
      </div>

      <div class="medium-12 columns end padding-r-s margin-b-m small-padding-h-z large-4"
        v-if="isAirportPickup">
        <div :class="{'field-group':true, 'field-error':!!validation.airlineFlight}">
          <label class="field-label">{{ $t('serviceSelect.flightInfo') }}</label>
          <div class="field-with-loader">
            <input :class="{'field-text':true, 'disabled':airlineLoading || !enableFlightField, 'highlight':highlight === 'airlineFlight'}"
              placeholder="e.g. UA 1234" @click="showFlightModal" type="text"
              v-model="airlineFlight" readonly />
            <i class="fas fa-spinner fa-spin" v-show="airlineLoading"></i>
          </div>
          <div class="text-right error-box"><span class="field-error-message">{{ validation.airlineFlight }}</span></div>
        </div>
      </div>

      <div class="medium-12 columns end padding-r-s margin-b-m small-padding-h-z large-4"
        v-if="leg !== 'leg2' && !isAsap">
        <div :class="{'field-group':true, 'field-error':!!validation.passenger}">
          <label class="field-label">{{ $t('serviceSelect.pax') }}</label>
          <select :class="{'field-text field-select':true, 'highlight':highlight === 'passenger'}"
            v-model="passenger" @focus="onFocus('passenger')">
            <option disabled value="-1">{{ $t('serviceSelect.choose') }}</option>
            <option v-for="n in 9" :key="n">{{ n }}</option>
            <option v-for="n in 5" :key="n + 9" v-if="mtech.enabled">{{ n + 9 }}</option>
          </select>
          <div class="text-right"><span class="field-error-message">{{ validation.passenger }}</span></div>
        </div>
      </div>

      <div class="medium-12 columns end padding-r-s margin-b-m small-padding-h-z large-4"
        v-if="leg !== 'leg2' && !isAsap">
        <div :class="{'field-group':true, 'field-error':!!validation.luggage}">
          <label class="field-label">
            {{ $t('serviceSelect.luggage') }}
            <i class="fas fa-info-circle margin-l-xs"
              @mouseover="focusLuggage" @mouseleave="blurLuggage"></i>

            <transition name="slide-up">
              <div class="luggage-tool-tip" v-show="showLuggageTooltip">
                {{ $t('serviceSelect.carryOns') }}
              </div>
            </transition>
          </label>
          <select :class="{'field-text field-select':true, 'highlight':highlight === 'luggage'}"
            v-model="luggage" @focus="onFocus('luggage')" ref="luggage">
            <option disabled value="-1">{{ $t('serviceSelect.choose') }}</option>
            <option v-for="n in 19" :value="n - 1" :key="n">{{ n - 1 }}</option>
          </select>
          <div class="text-right"><span class="field-error-message">{{ validation.luggage }}</span></div>
        </div>
      </div>

      <div v-if="!isAirportPickup" class="medium-12 large-4 columns end padding-r-s margin-b-m small-padding-h-z">
        <div class="field-group">
          <label class="field-label">
            {{ $t('serviceSelect.aptGate') }}
            <span>{{ $t('serviceSelect.optional') }}</span>
          </label>
          <input :class="{'field-text':true, 'highlight':highlight === 'apt'}"
            type="text" placeholder="e.g. Suite 123" ref="apt"
            @change="updateApt" @focus="onFocus('apt')" v-model="apt" />
        </div>
      </div>
    </div>

    <div class="row" v-else>
      <div :class="['medium-12 column padding-r-s margin-b-m small-padding-h-z', leg !== 'leg2' ? 'large-4' : 'large-6']">
        <div :class="{'field-group':true, 'field-error':!!validation.pickupDate}">
          <label class="field-label">
            <div v-if="isAirportPickup && !isLyftBooking">{{ $t('serviceSelect.flightArrive') }}</div>
            <div v-else-if="isAirportDropoff && !isLyftBooking">{{ $t('serviceSelect.flightDepart') }}</div>
            <div v-else>{{ $t('serviceSelect.whenRide') }}</div>
          </label>
          <input type="text" :placeholder="pickupDatePlaceholder" @click="openPicker"
            :class="{'field-text':true, 'highlight':highlight === 'pickupDate'}"
            v-model="pickupDate" ref="datepicker" readonly />
          <div class="text-right error-box"><span class="field-error-message">{{ validation.pickupDate }}</span></div>
        </div>
      </div>

      <div v-if="(isAirportPickup || isAirportDropoff) && !isLyftBooking" :class="['medium-12 column padding-r-s margin-b-m small-padding-h-z', leg !== 'leg2' ? 'large-4' : 'large-6']">
        <div :class="{'field-group':true, 'field-error':!!validation.airlineFlight}">
          <label class="field-label">{{ $t('serviceSelect.flightInfo') }}</label>
          <div class="field-with-loader">
            <input :class="{'field-text':true, 'disabled':airlineLoading || !enableFlightField, 'highlight':highlight === 'airlineFlight'}"
              placeholder="e.g. UA 1234" @click="showFlightModal" type="text"
              v-model="airlineFlight" readonly />
            <i class="fas fa-spinner fa-spin" v-show="airlineLoading"></i>
          </div>
          <div class="text-right error-box"><span class="field-error-message">{{ validation.airlineFlight }}</span></div>
        </div>
      </div>

      <div :class="['small-6 column padding-r-s margin-b-m end', !isAirportPickup && isAirportDropoff ? 'large-4' : 'large-2']"
        v-if="leg !== 'leg2'">
        <div :class="{'field-group':true, 'field-error':!!validation.passenger}">
          <label class="field-label">{{ $t('serviceSelect.pax') }}</label>
          <select :class="{'field-text field-select':true, 'highlight':highlight === 'passenger'}"
            v-model="passenger" @focus="onFocus('passenger')">
            <option disabled value="-1">{{ $t('serviceSelect.choose') }}</option>
            <option v-for="n in 9" :key="n">{{ n }}</option>
            <option v-for="n in 5" :key="n + 9" v-if="mtech.enabled">{{ n + 9 }}</option>
          </select>
          <div class="text-right"><span class="field-error-message">{{ validation.passenger }}</span></div>
        </div>
      </div>
      <div :class="['small-6 column padding-r-s margin-b-m small-padding-h-z end', !isAirportPickup && isAirportDropoff ? 'large-4' : 'large-2']"
        v-if="leg !== 'leg2'">
        <div :class="{'field-group':true, 'field-error':!!validation.luggage}">
          <label class="field-label">
            {{ $t('serviceSelect.luggage') }}
            <i class="fas fa-info-circle margin-l-xs"
              @mouseover="focusLuggage" @mouseleave="blurLuggage"></i>

            <transition name="slide-up">
              <div class="luggage-tool-tip" v-show="showLuggageTooltip">
                {{ $t('serviceSelect.carryOns') }}
              </div>
            </transition>
          </label>
          <select :class="{'field-text field-select':true, 'highlight':highlight === 'luggage'}"
            v-model="luggage" @focus="onFocus('luggage')" ref="luggage">
            <option disabled value="-1">{{ $t('serviceSelect.choose') }}</option>
            <option v-for="n in 19" :value="n - 1" :key="n">{{ n - 1 }}</option>
          </select>
          <div class="text-right"><span class="field-error-message">{{ validation.luggage }}</span></div>
        </div>
      </div>

      <div v-if="leg !== 'leg2' && !isAirportPickup" class="medium-12 large-4 column padding-r-s margin-b-m small-padding-h-z">
        <div class="field-group">
          <label class="field-label">
            {{ $t('serviceSelect.aptGate') }}
            <span>{{ $t('serviceSelect.optional') }}</span>
          </label>
          <input :class="{'field-text':true, 'highlight':highlight === 'apt'}"
            type="text" placeholder="e.g. Suite 123" ref="apt"
            @change="updateApt" @focus="onFocus('apt')" v-model="apt" />
        </div>
      </div>

      <div class="small-12 column" v-show="leg === 'leg1'">
        <span class="arrival-time-label">* {{ $t('serviceSelect.arrivalTimes') }}</span>
      </div>
      <transition name="slide-up2">
        <div class="small-12 column pickup-date" v-show="leg === 'leg2' && suggestedPickupDate && (isAirportPickup || isAirportDropoff)">
          <span>
            {{ $t('serviceSelect.forReturnTrip') }}
            <a @click="customPickupTime">
              <b class="und">{{ suggestedPickupDate }}</b>
              <i class="fas fa-edit"></i>
            </a>
          </span>
        </div>
      </transition>
    </div>
  </div>
</template>

<script type="text/javascript">
/* global CONFIG */
import moment from 'moment-timezone'
import featureToggle from '@/modules/api/featureToggle'
import LocationInput from '../SearchForm/LocationInput'
import ApiQuote from '@/modules/api/quote'

export default {
  name: 'ride-details',
  props: ['leg'],
  components: { LocationInput },
  data () {
    return {
      pickup: '',
      dropoff: '',
      pickupText: '',
      dropoffText: '',
      pickupPlaceId: null,
      dropoffPlaceId: null,
      airlineFlight: '',
      apt: '',
      validation: {
        pickupDate: '',
        airlineFlight: '',
        passenger: '',
        luggage: ''
      },
      showLuggageTooltip: false,
      tooltipDelay: '',
      showLocs: true,
      charterValidation: {
        startDate: '',
        endDate: ''
      }
    }
  },
  computed: {
    rsvpData () { return this.$store.state.reservation },
    pickupExtra () {
      return {
        brandId: 13,
        field: 'pickup',
        placeholder: 'e.g. Airport, Home etc.',
        hasExtraStop: true,
        type: 'ground'
      }
    },
    dropoffExtra () {
      return {
        brandId: 13,
        field: 'dropoff',
        placeholder: 'e.g. Airport, Home etc.',
        type: 'ground'
      }
    },
    isAirportPickup () { return this.rsvpData[this.leg].isAirportPickup },
    isAirportDropoff () { return this.rsvpData[this.leg].isAirportDropoff },
    leg1Asap () { return this.rsvpData.leg1.isAsap },
    isLyftBooking () { return this.leg1Asap || this.rsvpData.lyft.enabled },
    isAsap () { return this.rsvpData[this.leg].isAsap },
    suggestedPickupDate () {
      let rsvp = this.rsvpData[this.leg]
      return this.suggestedPickupTime(rsvp.selectedService).val
    },
    passenger: {
      get: function () { return this.rsvpData[this.leg].passenger },
      set: function (value) {
        this.$store.commit('reservation/setPassenger', {
          leg: this.leg,
          value: parseInt(value)
        })
        if (this.leg === 'leg1') {
          this.$store.commit('reservation/setPassenger', {
            leg: 'leg2',
            value: parseInt(value)
          })
        }
        this.$emit('nextHighlight')
        if (this.mtech.enabled) {
          this.getQuote()
        }

        if (CONFIG.Env == 'production' && window.gtag) { // eslint-disable-line
          gtag('event', `Passengers`, { // eslint-disable-line
            value: value
          })
        }
      }
    },
    luggage: {
      get: function () { return this.rsvpData[this.leg].luggage },
      set: function (value) {
        let self = this
        self.$store.commit('reservation/setLuggage', {
          leg: self.leg,
          value: parseInt(value)
        })
        if (this.leg === 'leg1') {
          this.$store.commit('reservation/setLuggage', {
            leg: 'leg2',
            value: parseInt(value)
          })
        }
        self.$nextTick(function () { self.$refs.luggage.blur() })
        self.$emit('nextHighlight')

        if (CONFIG.Env == 'production' && window.gtag) { // eslint-disable-line
          gtag('event', `Check-in Luggage`, { // eslint-disable-line
            value: value
          })
        }
      }
    },
    pickupDate () {
      let rsvp = this.rsvpData[this.leg]
      if (rsvp.isAsap) { return 'ASAP' }
      return this.isAirportPickup || this.isAirportDropoff ? rsvp.flightDate : rsvp.pickupDate
    },
    // pickupDatePlaceholder () { return this.leg === 'leg1' && !this.mtech.enabled ? this.$t('serviceSelect.scheduledAsap') : this.$t('serviceSelect.scheduled') },
    pickupDatePlaceholder () { return this.$t('serviceSelect.scheduled') },
    _flightInfo () {
      return {
        airline: this.rsvpData[this.leg].airline,
        flightNo: this.rsvpData[this.leg].flightNo
      }
    },
    airlineLoading () { return this.rsvpData.airlineLoading },
    highlight () { return this.rsvpData.highlight[this.leg] },
    enableFlightField () {
      let rsvp = this.rsvpData[this.leg]
      return rsvp.isAsap === true || (rsvp.isAsap === false && !!rsvp.flightDate)
    },
    arrivalDepartureLabel () { return this.$settings.get('ArrivalDepartureLabel') === '1' },
    _pickupTimeOffsets () { return this.$settings.get('PickupTimeOffsets') },
    offsets () { return this.rsvpData.offSets },
    extraStop () { return this.rsvpData.extraStop[this.leg] },
    _isAsap () { return this.rsvpData[this.leg].isAsap },
    charter () { return this.rsvpData.charter },
    serviceLoading () { return this.rsvpData.serviceLoading },
    mtech () { return this.rsvpData.mtech },
    _pickupDate () { return this.rsvpData[this.leg].pickupDate }
  },
  methods: {
    onFocus (field) {
      this.$store.commit('reservation/setHighlight', {
        leg: this.leg,
        value: field
      })
    },
    openPicker () {
      let self = this
      self.$nextTick(function () { self.$refs.datepicker.blur() })

      self.$store.commit('reservation/setActiveLeg', self.leg)
      if (self.charter.enabled) {
        self.$store.commit('reservation/setCharterData', {datePicker: 'flightDate'})
      }
      self.$store.commit('serviceSelection/showDateTimePicker')
      self.$store.commit('reservation/setHighlight', {
        leg: self.leg,
        value: 'pickupDate'
      })
    },
    showFlightModal () {
      if (!this.enableFlightField) {
        this.validation.pickupDate = this.$t('serviceSelect.selectFlightTime')
        return
      }
      this.$store.commit('reservation/setActiveLeg', this.leg)
      this.$store.commit('reservation/setShowFlightModal', true)
      this.$store.commit('reservation/setHighlight', {
        leg: this.leg,
        value: 'airlineFlight'
      })
    },
    updateApt () {
      this.$store.commit('reservation/setApt', {
        leg: this.leg,
        value: this.apt
      })
      this.$emit('nextHighlight')
    },
    validate () {
      let rsvp = this.rsvpData[this.leg]
      this.validation = {
        pickupDate: '',
        airlineFlight: '',
        passenger: '',
        luggage: ''
      }
      if ((!rsvp.pickupDate && !this.isAsap) || (this.pickupDate.trim() === '' && !this.charter.enabled)) {
        if (rsvp.isAirportPickup || rsvp.isAirportDropoff) {
          if (!rsvp.flightDate) {
            this.validation.pickupDate = this.$t('common.fieldEmpty')
          }
        } else {
          this.validation.pickupDate = this.$t('common.fieldEmpty')
        }
      }
      if (rsvp.isAirportPickup || (CONFIG.featureToggle.requiredDropoffAirport &&
        rsvp.isAirportDropoff)
      ) {
        if ((!rsvp.flightNo || rsvp.flightNo.trim() === '') && !this.isAsap) {
          this.validation.airlineFlight = this.$t('serviceSelect.inputFlightNo')
        }
        if ((!rsvp.airline || rsvp.airline.trim() === '') && !this.isAsap) {
          this.validation.airlineFlight = this.$t('common.fieldEmpty')
        }
      }
      if (!this.passenger && !this.charter.enabled) {
        this.validation.passenger = this.$t('common.fieldEmpty')
      }
      if (this.luggage === '' && !this.charter.enabled) {
        this.validation.luggage = this.$t('common.fieldEmpty')
      }

      if (this.charter.enabled) {
        if (!this.charter.startDate) {
          this.charterValidation.startDate = this.$t('common.fieldEmpty')
        }
        if (!this.charter.endDate) {
          this.charterValidation.endDate = this.$t('common.fieldEmpty')
        }
      }

      if (!this.validation.pickupDate && !this.validation.airlineFlight) {
        this.$emit('noErrors')
      }
    },
    focusLuggage () {
      let self = this
      if (self.tooltipDelay) { clearTimeout(self.tooltipDelay) }
      self.showLuggageTooltip = true
    },
    blurLuggage () {
      let self = this
      self.tooltipDelay = setTimeout(function () {
        self.showLuggageTooltip = false
      }, 500)
    },
    suggestedPickupTime (service, bypass = false) {
      let rsvp = this.rsvpData
      if (!rsvp.suggestedTime[this.leg].time) { return '' }

      let date = moment(rsvp.suggestedTime[this.leg].time, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      if (rsvp[this.leg].isAirportPickup) {
        let delay = rsvp.suggestedTime[this.leg].isDomestic ? 'ArrivalDomesticDelay' : 'ArrivalInternationalDelay'
        date.add(this.$settings.get(delay), 'minutes')
      } else if (rsvp[this.leg].isAirportDropoff) {
        let delay = rsvp.suggestedTime[this.leg].isDomestic ? 'DepartureDomesticBuffer' : 'DepartureInternationalBuffer'
        let eta = Math.ceil(rsvp[this.leg].eta / 60)
        let btt = Math.ceil(eta * 1.15)
        if (service === 'shared' || (rsvp[this.leg].services[service] && rsvp[this.leg].services[service].isShared)) {
          btt = Math.ceil(eta * (1 + (this.$settings.get('OfferLimitThreshold') / 100)))
          btt = (btt - (btt % 5)) + 5
        }
        let timeDelay = Math.ceil(parseFloat(this.$settings.get(delay)))
        date.subtract(timeDelay + btt, 'minutes')
        date.subtract(parseInt(date.format('mm')) % 5, 'minutes')
      }

      // !date.isBefore(moment().add(this.$settings.get('WebTimeBuffer'), 'minutes')) ? date.format('MM/DD/YYYY ' + featureToggle.timeFormat(this, false)) : ''
      if (rsvp.customPickupTime[this.leg] && !bypass) {
        date = moment(rsvp[this.leg].pickupDate, 'MM/DD/YYYY ' + featureToggle.timeFormat(this, false))
      } else {
        if (this.offsets['a' + date.format('e')] && rsvp[this.leg].isAirportDropoff) {
          let found = false
          this.offsets['a' + date.format('e')].forEach(function (data, index) {
            let time = moment(date.format('HHmm'), 'HHmm')
            let start = moment(data.start, 'HHmm')
            let end = moment(data.end, 'HHmm')
            if (time.isBetween(start, end) && !found) {
              found = true
              if (data.value.indexOf('-') > -1) {
                date.subtract(data.value.substring(1, data.value.length), 'minutes')
              } else {
                date.add(data.value, 'minutes')
              }
            }
          })
        }
      }
      return {
        val: date.format('MM/DD/YYYY ' + featureToggle.timeFormat(this, false)),
        isAllowed: !date.isBefore(moment().add(this.$settings.get('WebTimeBuffer'), 'minutes'))
      }
    },
    customPickupTime () {
      let self = this
      let code = self.$store.state.reservation[self.leg].selectedService
      self.$store.commit('reservation/setActiveLeg', self.leg)
      self.$store.commit('serviceSelection/setSuggestedTime', this.suggestedPickupTime(code, true).val)
      self.$store.commit('serviceSelection/setForPickupDate', true)
      self.$store.commit('serviceSelection/setCustomPickupDateDefault', this.suggestedPickupTime(code).val)
      self.$store.commit('serviceSelection/showDateTimePicker')
    },
    setPickupTimeOffsets () {
      let self = this
      let PickupTimeOffsets = this.$settings.get('PickupTimeOffsets')
      if (PickupTimeOffsets) {
        let split = PickupTimeOffsets.split(',')
        split.forEach(function (val, index) {
          if (!self.offsets['a' + val.substring(0, 1)]) {
            self.$store.commit('reservation/setOffSets', {
              id: 'a' + val.substring(0, 1),
              value: []
            })
          }
          self.$store.commit('reservation/addOffSets', {
            id: 'a' + val.substring(0, 1),
            value: {
              start: val.substring(1, 5),
              end: val.substring(5, 9),
              value: val.substring(9, val.length)
            }
          })
        })
      }
    },
    getQuote () {
      let self = this
      let rsvpData = this.rsvpData[this.leg]
      let pickupDate = rsvpData.pickupDate || rsvpData.flightDate
      pickupDate = pickupDate || moment().format('MM/DD/YYYY')
      pickupDate = moment(pickupDate, 'MM/DD/YYYY').format('YYYY-MM-DD')
      let quoteData = {
        brandId: this.$store.state.brandId,
        date: pickupDate,
        leg: this.leg,
        pickup: {
          lat: rsvpData.pickup.lat,
          lng: rsvpData.pickup.lng
        },
        dropoff: {
          lat: rsvpData.dropoff.lat,
          lng: rsvpData.dropoff.lng
        },
        asap: this._isAsap
      }

      self.$store.commit('reservation/setServiceLoading', true)
      quoteData = {
        brandId: self.$store.state.brandId,
        leg: self.leg,
        pickupDate: pickupDate,
        pickup: {
          lat: rsvpData.pickup.lat,
          lng: rsvpData.pickup.lng
        },
        dropoff: {
          lat: rsvpData.dropoff.lat,
          lng: rsvpData.dropoff.lng
        },
        order: 'priority',
        asap: this._isAsap
      }
      quoteData.pickup = rsvpData.pickup
      quoteData.dropoff = rsvpData.dropoff
      self.extraStop.forEach((stop, index) => {
        if (!quoteData.extraStops) {
          quoteData.extraStops = []
        }
        quoteData.extraStops.push({
          lat: stop.location.lat,
          lng: stop.location.lng
        })
      })
      if (self.charter.enabled) {
        let startDate = moment(self.charter.startDate, 'MM/DD/YYYY hh:mmA')
        let endDate = moment(self.charter.endDate, 'MM/DD/YYYY hh:mmA')
        let duration = moment.duration(endDate.diff(startDate))
        quoteData = {
          brandId: self.$store.state.brandId,
          pickupDate: startDate.format('YYYY-MM-DD'),
          etaInSec: duration.asSeconds(),
          pickup: self.charter.pickup,
          isCharter: true,
          leg: 'leg1'
        }
      }
      ApiQuote.getQuote2(self, quoteData, (res) => {
        self.$store.commit('reservation/setServiceLoading', false)
        self.$store.commit('reservation/resetServiceEta')

        if (!res.error) {
          if (Object.keys(res.geofences).length && (res.geofences.pickup.type === 'airport' || res.geofences.dropoff.type === 'airport')) {
            self.getAirlineList()
          }
        }

        if (!res.error && self.mtech.enabled) {
          let serviceExist = false
          res.mtech.services.forEach(service => {
            if (service.product.code === rsvpData.selectedService) {
              serviceExist = true
            }
          })
          if (!serviceExist) {
            self.$store.commit('reservation/setSelectedService', {
              leg: self.leg,
              value: ''
            })
          }
          if (rsvpData.selectedService) {
            this.getQuoteMtechService()
          }
        }
      })
    },
    openCharterPicker (field) {
      let self = this
      self.$nextTick(function () { self.$refs[field].blur() })

      self.$store.commit('reservation/setActiveLeg', self.leg)
      self.$store.commit('reservation/setCharterData', {datePicker: field})
      self.$store.commit('serviceSelection/showDateTimePicker')
      self.$store.commit('reservation/setHighlight', {
        leg: self.leg,
        value: field
      })
    },
    getQuoteMtechService () {
      let self = this
      let rsvpData = this.rsvpData[this.leg]

      let pickupDate = rsvpData.pickupDate
      if (rsvpData.flightDate && (rsvpData.isAirportDropoff || rsvpData.isAirportPickup)) {
        pickupDate = rsvpData.flightDate
      }
      pickupDate = pickupDate || moment().format('MM/DD/YYYY hh:mmA')
      pickupDate = moment(pickupDate, 'MM/DD/YYYY hh:mmA')
      let quoteData = {
        asap: false,
        brandId: self.$store.state.brandId,
        leg: 'leg1',
        pickupDate: pickupDate.format('YYYY-MM-DD'),
        pickupTime: pickupDate.format('HH:mm:ss'),
        pickup: rsvpData.pickup,
        dropoff: rsvpData.dropoff,
        order: 'priority',
        passengers: rsvpData.passenger,
        mtechServiceType: rsvpData.selectedService
      }
      quoteData.pickup.text = quoteData.pickup.title
      quoteData.dropoff.text = quoteData.dropoff.title
      if (rsvpData.flightDate) {
        quoteData.pickupTimestamp = moment.tz(rsvpData.flightDate, 'MM/DD/YYYY hh:mmA', rsvpData.timezone).format('X')
      } else {
        quoteData.pickupTimestamp = moment.tz(`${quoteData.pickupDate} ${quoteData.pickupTime}`, 'YYYY-MM-DD HH:mm', rsvpData.timezone).format('X')
      }

      self.$store.commit('reservation/setServiceLoading', true)
      let timeNow = moment()
      self.$sdk.post(self, '/quotation', quoteData, res => {
        self.sendQuoteTime(timeNow, moment(), res.quoteId, quoteData)
        self.$store.commit('reservation/setServiceLoading', false)
        if (!res.error) {
          self.$store.commit('reservation/setQuoteId', res.quoteId)
          if (res.mtech.services.length) {
            let mtech = res.mtech
            let mtechStore = self.rsvpData.mtech
            let service = res.mtech.services[0]
            let price = service.price

            let hasDiscount = false
            let discountVal = 0
            let totalBeforeDc = 0
            let baseFare = price.amount
            service.price.breakdown.forEach(x => {
              if (x.description === 'Base rate') {
                baseFare = x.amount
              }
              if (x.description === 'Discount') {
                hasDiscount = true
                discountVal = x.amount
              }
              if (x.amount > 0) {
                totalBeforeDc += x.amount
              }
            })

            let addMarkup = 0
            if (mtech.markup.value) {
              addMarkup = mtechStore.markup.value
              if (mtech.markup.type === '%') {
                addMarkup = baseFare * (mtech.markup.value / 100)
              }
              addMarkup = parseFloat(addMarkup.toFixed(2))
            }

            self.$store.commit('reservation/setMtechNewPricing', {
              code: rsvpData.selectedService,
              val: {
                baseFare: baseFare,
                amount: price.amount,
                addMarkup: addMarkup,
                breakdown: price.breakdown,
                hasDiscount: hasDiscount,
                discountVal: Math.abs(discountVal),
                totalBeforeDc: totalBeforeDc
              }
            })
          }
          self.$nextTick().then(() => {
            self.$forceUpdate()
          })
        }
      }, '2.0')
    },
    validateChk (type, field) {
      let target = type === 'charter' ? this.charter[field] : this[field]
      let xtype = type === 'charter' ? 'charterValidation' : 'validation'
      if (this[xtype][field] && !!target) {
        this[xtype][field] = ''
      }

      this.validate()
    },
    sendQuoteTime (start, end, quoteId, param) {
      let self = this
      let store = self.$store.state
      let duration = end.diff(start, 'milliseconds') / 1000
      self.$sdk.opt('/quotation/time', {
        token: store.user.token,
        brandId: store.brandId,
        quotationId: quoteId,
        responseTime: duration,
        deviceType: 'ww',
        data: JSON.stringify(param)
      }, res => {
        if (res.error) console.log('ERROR quotation time')
      })
    },
    locationNewValue (value) {
      let self = this
      if (value.fromMap && !value.keyInput.trim()) return

      if (value.field === 'pickup') {
        self.pickupText = value.keyInput
        // this.$emit('pickupChange')
        if (value.keyInput.trim() === '') {
          self.$store.commit('reservation/setPickup', {
            leg: value.leg,
            value: ''
          })
        } else {
          if (!value.blurStop) {
            this.validation.pickup = ''
            if (self.dropoffText !== '' && !value.blurStop) {
            // self.validateGeo(null, 'leg1')
              self.getQuote()
            }
          }
        }
      } else if (value.field === 'extraStop') {
        self.$store.commit('reservation/setExtraStopData', {
          leg: value.leg,
          id: value.id,
          fields: { text: value.keyInput }
        })
        if (value.keyInput.trim() === '') {
          self.$store.commit('reservation/setExtraStopLocationData', {
            leg: value.leg,
            id: value.id,
            value: ''
          })
        } else {
          if (!value.blurStop) {
            self.$store.commit('reservation/setExtraStopData', {
              leg: value.leg,
              id: value.id,
              fields: { error: '' }
            })
            let index = self.extraStop.leg1.findIndex(x => x.id === value.id)
            if (index === self.extraStop.leg1.length - 1) {
              if (this.$refs.dropoff.focus) {
                this.validation.extraStop = ''
                this.$refs.dropoff.focus()
              }
            } else if (index > -1) {
              if (this.$refs[`extraStop${self.extraStop.leg1[index + 1].id}`][0].focus) {
                this.$refs[`extraStop${self.extraStop.leg1[index + 1].id}`][0].focus()
              }
            }
          }
          let index = self.extraStop.leg1.findIndex(x => x.id === value.id)
          let indexLeg2 = self.extraStop.leg2.findIndex(x => x.id === value.id)
          if (index > -1 && indexLeg2 === -1) {
            self.$store.commit('reservation/prependExtraStop', {
              leg: 'leg2',
              value: self.extraStop.leg1[index]
            })
          }
        }
      } else {
        self.dropoffText = value.keyInput
        if (value.keyInput.trim() === '') {
          self.$store.commit('reservation/setDropoff', {
            leg: value.leg,
            value: ''
          })
        } else {
          this.validation.dropoff = ''
          if (self.pickupText !== '' && !value.blurStop) {
            // self.validateGeo(null, 'leg1')
            self.getQuote()
          }
        }
      }
    },
    getAirlineList () {
      let self = this

      let pickup = self.rsvpData.leg1.pickup
      let dropoff = self.rsvpData.leg1.dropoff
      if (typeof pickup.geofenceType === 'undefined' ||
        (pickup.geofenceType !== 'airport' && dropoff.geofenceType !== 'airport')
      ) { return }

      if (self.airlineLoading || self.rsvpData.airlineGID === pickup.geofenceId) { return }
      self.$store.commit('reservation/setAirlineLoading', true)

      let geofenceId = pickup.geofenceId
      if (pickup.geofenceType !== 'airport' && dropoff.geofenceType === 'airport') { geofenceId = dropoff.geofenceId }
      self.$sdk.get(self, '/geofences/' + geofenceId + '/airlines', {}, res => {
        self.$store.commit('reservation/setAirlineLoading', false)
        if (!res.error) {
          let airlines = []
          res.list.forEach(function (data) {
            airlines.push({
              code: data.code,
              name: data.name,
              label: '(' + data.code + ') ' + data.name
            })
          })
          self.$store.commit('reservation/setAirlineList', {
            id: geofenceId,
            list: airlines
          })
        }
      }, '/1.1')
    }
  },
  watch: {
    pickupDate: function (value) {
      this.$emit('nextHighlight')
      this.validateChk('validation', 'pickupDate')
    },
    _flightInfo: function (value) {
      if (!!value.airline && !!value.flightNo) {
        this.airlineFlight = value.airline + ' ' + value.flightNo
      }
      this.$emit('nextHighlight')
    },
    _pickupTimeOffsets: function (value) { if (value) this.setPickupTimeOffsets() },
    extraStop () {
      this.getQuote()
    },
    'charter.startDate': function () {
      this.$store.commit('reservation/setHighlight', {
        leg: 'leg1',
        value: 'endDate'
      })
      this.validateChk('charter', 'startDate')
    },
    'charter.endDate': function (val) {
      if (val && this.leg === 'leg1') {
        this.getQuote()
      }
      this.validateChk('charter', 'endDate')
    },
    airlineFlight: function () {
      this.validateChk('validation', 'airlineFlight')
    },
    _pickupDate (val) {
      if (val) this.getQuote()
    }
  },
  mounted () {
    this.setPickupTimeOffsets()

    this.pickup = this.rsvpData.leg1.pickup.title
    this.pickupText = this.rsvpData.leg1.pickup.title
    this.dropoff = this.rsvpData.leg1.dropoff.title
    this.dropoffText = this.rsvpData.leg1.dropoff.title
    if (this.leg === 'leg2') {
      this.pickup = this.rsvpData.leg1.dropoff.title
      this.pickupText = this.rsvpData.leg1.dropoff.title
      this.dropoff = this.rsvpData.leg1.pickup.title
      this.dropoffText = this.rsvpData.leg1.pickup.title
    }
    // this.showLocs = this.rsvpData.extraStop[this.leg].length > 0

    let rsvp = this.$store.state.reservation[this.leg]
    this.airlineFlight = rsvp.airline
    if (rsvp.flightNo) {
      this.airlineFlight += ' ' + rsvp.flightNo
    }

    this.apt = rsvp.apt
  }
}
</script>

<style lang="scss" scoped>
.text-right{ text-align: right; }
.clear{ clear: both; }

.error-box{
  position: absolute;
  right: 0;
  top: 95%;
}
.field-error-message{
  float: none;
  position: relative;
  top: -2px!important;
}

.field-text {
  margin-bottom: 0;
  &[readonly='readonly']{ cursor: pointer; }
  &.disabled{ background: #eee; }
}

.field-label{
  position: relative;
  span{ color: #bbb; }
}

.field-with-loader{
  position: relative;

  .fas{
    position: absolute;
    top: 8px;
    right: 5px;
    font-size: 24px;
  }
}

.arrival-time-label{
  display: block;
  font-size: 12px;
  color: #999;
  text-align: center;
}

.luggage-tool-tip{
  position: absolute;
  bottom: calc(100% + 8px);
  left: 30px;
  background:#7b7b7b;
  color: #fff;
  padding: 5px 8px;
  font-size: 11px;
  line-height: 1;

  &:before{
    content: '';
    position: absolute;
    top: 100%;
    left: 37px;
    border-style: solid;
    border-width: 5px 5px 0 5px;
    border-color: #7b7b7b transparent transparent transparent;
  }
}

.pickup-date{
  text-align: center;
  font-size: 14px;
  margin-top: 5px;
  transform-origin: top center;
}
.und{ text-decoration: underline; }

.extra-stops{
  .es-notice{
    display: block;
    clear: both;
    font-size: 14px;
    color: #999;
    font-style: italic;
    padding-top: 10px;
  }
}

.slide-up2-enter-active, .slide-up2-leave-active { transition: all .3s ease; }
.slide-up2-enter, .slide-up-leave-to {
  transform: rotateX(-90deg);
  opacity: 0;
}

.slide-up-enter-active, .slide-up-leave-active { transition: all .25s ease; }
.slide-up-enter, .slide-up-leave-to {
  transform: translateY(15px);
  opacity: 0;
}
</style>



// WEBPACK FOOTER //
// RideDetails.vue?dfacd918